import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, CloudOff, RefreshCw, Check, AlertCircle, X } from 'lucide-react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { getPendingCount } from '@/utils/offlineStorage';
import { syncAllPending } from '@/utils/syncService';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

export function OfflineBanner() {
  const { isOffline, wasOffline, clearWasOffline } = useOfflineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const { toast } = useToast();
  const lastPendingCountRef = useRef(0);

  // Check pending count periodically
  useEffect(() => {
    const checkPending = async () => {
      const count = await getPendingCount();
      setPendingCount(count);
      // Only re-show banner if NEW items were added (count increased)
      if (count > lastPendingCountRef.current) {
        setDismissed(false);
      }
      lastPendingCountRef.current = count;
    };
    
    checkPending();
    const interval = setInterval(checkPending, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (wasOffline && !isOffline && pendingCount > 0) {
      handleSync();
    }
  }, [wasOffline, isOffline, pendingCount]);

  // Auto-dismiss after sync completes (success or fail)
  useEffect(() => {
    if (syncFailed && !isSyncing) {
      const timeout = setTimeout(() => {
        setDismissed(true);
        setSyncFailed(false);
      }, 8000); // Auto-dismiss after 8 seconds
      return () => clearTimeout(timeout);
    }
  }, [syncFailed, isSyncing]);

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncFailed(false);
    try {
      const result = await syncAllPending();
      
      if (result.synced > 0) {
        setShowSyncSuccess(true);
        setTimeout(() => setShowSyncSuccess(false), 3000);
        toast({
          title: "Data Tersinkron",
          description: `${result.synced} item berhasil disinkronkan`,
        });
      }
      
      // Show toast for permanently removed items (hit max retries)
      if (result.removed > 0) {
        setSyncFailed(true);
        toast({
          title: "Item Dihapus",
          description: `${result.removed} item dihapus setelah gagal 3 kali.`,
          variant: "destructive",
        });
      }
      
      // Show warning for items still in retry queue
      if (result.retryingFailed > 0) {
        toast({
          title: "Akan Dicoba Lagi",
          description: `${result.retryingFailed} item gagal disinkronkan. Akan dicoba lagi nanti.`,
        });
      }
      
      // Refresh pending count and update ref to match
      const count = await getPendingCount();
      setPendingCount(count);
      lastPendingCountRef.current = count;
      clearWasOffline();
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncFailed(true);
      toast({
        title: "Sinkronisasi Gagal",
        description: "Tidak dapat menyinkronkan data. Coba lagi nanti.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {/* Offline Banner */}
      {isOffline && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-0 left-0 right-0 z-40 bg-accent text-accent-foreground px-4 py-3 pt-safe shadow-lg"
        >
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <WifiOff className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Mode Offline</p>
                <p className="text-xs opacity-80">
                  Data akan disimpan dan disinkronkan saat online
                </p>
              </div>
            </div>
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 bg-accent-foreground/15 px-3 py-1.5 rounded-full">
                <CloudOff className="h-4 w-4" />
                <span className="text-xs font-medium">{pendingCount} pending</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Pending Sync Banner (when online with pending items) - DISMISSIBLE */}
      {!isOffline && pendingCount > 0 && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-[calc(env(safe-area-inset-top)+4rem)] left-4 right-4 z-40 pointer-events-none"
        >
          <div className="pointer-events-auto max-w-2xl mx-auto bg-primary text-primary-foreground px-4 py-3 rounded-xl shadow-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm">{pendingCount} Data Menunggu Sinkronisasi</p>
                  <p className="text-xs text-primary-foreground/80 truncate">
                    Data offline siap dikirim ke server
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="bg-primary-foreground/15 hover:bg-primary-foreground/20 text-primary-foreground border-0"
                >
                  {isSyncing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      Sync
                    </>
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/15"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Sync Success Toast */}
      {showSyncSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-0 left-0 right-0 z-40 bg-secondary text-secondary-foreground px-4 py-3 pt-safe shadow-lg"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Check className="h-5 w-5" />
            <p className="font-medium text-sm">Semua data berhasil disinkronkan!</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
